import Link from 'next/link';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import i18n, { SettingsType } from 'utils/i18n';
import Button from './button';
import MarkdownRender from './markdown-render';
import MetaInfo from './meta-info';
import MetaTags from './meta-tags';
import UnitItem from './unit-item';
import BookmarkButton from './bookmark-button';
import { moduleStatus, useUserData } from 'utils/user';
import { mqUntil } from 'styles/styles';

import ProgressHexagon from 'components-new/progress-hexagon';

export type ModuleItemProps = {
  module: Module;
  expanded: boolean;
  onToggle: () => void;
};

export const ModuleItem: React.FC<ModuleItemProps> = ({ module, expanded, onToggle }) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const { achievements } = useUserData();

  return (
    <StyledModuleItem className={`module  module-item`}>
      <ProgressHexagon status={moduleStatus(achievements, module)} />
      <div className="inner">
        <div className="image">
          {module.icon ? (
            <div className="header-icon">
              <Image
                src={module.icon.url}
                width={module.icon.width}
                height={module.icon.height}
                alt={module.icon.title}
                sizes="96px"
              />
            </div>
          ) : (
            <div className="empty-header-icon"></div>
          )}
        </div>
        <div className="content">
          <Link href={module.meta.path}>
            <h3 className="header">{module.title}</h3>
          </Link>
          <MetaInfo readingTime={module.meta.readingTime} type="MODULE" unitsCount={module.units.length} />
          <MetaTags tags={module.meta.tags} />
          <MarkdownRender markdown={module.body} />

          <Button
            mode="transparent"
            size="small"
            iconName={expanded ? 'chevron-up' : 'chevron-down'}
            onClick={onToggle}
          >
            {dictionary.overview}
          </Button>

          {expanded && (
            <div className="units">
              {module.units.map((unit) => {
                const unitCompleted = !!achievements.find(
                  (ach) =>
                    ach.contentId === unit.meta.id &&
                    ach.type === (unit.meta.type === 'Quiz' ? 'QuizComplete' : 'UnitComplete')
                );

                return <UnitItem key={unit.meta.id} unit={unit} checked={unitCompleted} />;
              })}
            </div>
          )}
        </div>
      </div>
      <div className="footer">
        <BookmarkButton moduleId={module.meta.id} learningPathId={module.meta.parent?.id} />
      </div>
    </StyledModuleItem>
  );
};

export const StyledModuleItem = styled.div`
  background: var(--body-background);
  border: 1px solid var(--border-white-high-contrast);
  box-shadow: 0 1.6px 3.6px 0 var(--box-shadow-medium), 0 0.3px 0.9px 0 var(--box-shadow-light);
  display: flex;
  flex-direction: column;
  position: relative;

  .inner {
    display: flex;
    padding: 1.5rem;
    position: relative;
  }

  .xp-hexagon {
    right: 3rem;
    top: 3rem;

    ${mqUntil.mobile} {
      top: 1.5rem;
    }
  }

  .image {
    width: 96px;

    .empty-header-icon {
      width: 96px;
      height: 96px;
      background: #52b3c0;
      border: 3px solid #438c94;
      border-radius: 50%;
    }
  }

  .content {
    padding: 1.5rem;
    flex: 1 1 0;

    .h3 {
      font-size: 1.125rem;
      margin-bottom: 0;
    }

    .learning-objectives {
      margin: 1rem 0;
      font-size: 0.875rem;

      ul {
        margin: 16px 0;
        margin-left: 38px;
        li {
          list-style: disc none outside;
        }
      }
    }

    .buttons {
      display: flex;
      gap: 0.5rem;
      margin: 1rem 0;
    }
  }

  h3.header {
    font-size: 1.125rem;
    margin: 0 6rem 0 0;

    ${mqUntil.mobile} {
      margin: 0;
    }
  }

  .units {
    margin: 0.5rem 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .footer {
    padding: 0.25rem 1rem 0.25rem 0.5rem;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
`;

export default ModuleItem;
